import { Button, KpiTile } from '@platform/design-system';
import { RotateCw } from 'lucide-react';
import { usePageContext } from '../../usePageContext.ts';
import { seriesUnitOfRange } from '../../report-period.ts';
import { useDashboardStats } from '../dashboard-stats.ts';
import { useStockValueTotals } from '../stock-value/stock-value.ts';
import { tileOf } from '../overview-tiles.ts';
import { kpiDeltaOf } from './kpi-delta.ts';
import { useOverviewFormat } from './use-overview-format.ts';
import { usePerformanceFor } from './widget-queries.ts';
import type { WidgetProps } from './widget-props.ts';

const ICON_SIZE = 22;

export const KpiWidget = ({ widget, context }: WidgetProps<'kpi'>) => {
  const { t, locale } = usePageContext();
  const format = useOverviewFormat();
  const tile = tileOf(widget.settings.tile);
  const performance = usePerformanceFor(context, {
    unit: seriesUnitOfRange(context.range),
    enabled: tile.source === 'report',
  });
  const stats = useDashboardStats(context.api, context.company);
  const stock = useStockValueTotals(context, tile.source === 'stock');
  const figures = { report: performance.data, stats: stats.data, stock: stock.data };
  const report = performance.data;
  const Icon = tile.icon;
  const source = { report: performance, stats, stock }[tile.source];
  if (source.isError)
    return (
      <KpiTile
        label={widget.title ?? t(`dashboard.overview.${tile.key}`)}
        icon={{ glyph: <Icon size={ICON_SIZE} />, tone: tile.tone }}
        hint={t('catalog.loadFailed')}
        action={
          <Button
            size="sm"
            variant="ghost"
            icon={<RotateCw size={16} aria-hidden="true" />}
            onClick={() => {
              void source.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        }
      />
    );
  return (
    <KpiTile
      label={widget.title ?? t(`dashboard.overview.${tile.key}`)}
      icon={{ glyph: <Icon size={ICON_SIZE} />, tone: tile.tone }}
      loading={source.isPending}
      value={tile.value(figures, format)}
      hint={tile.detail?.(figures, format)}
      trend={report === undefined ? undefined : tile.trend?.(report)}
      delta={kpiDeltaOf(tile, report, {
        locale,
        label: (value) => t('dashboard.widgets.deltaLabel', { value }),
      })}
    />
  );
};
